import React, { useRef, useEffect, useMemo } from "react";

import {
  getAnswersByRole,
  QuestionImplProps,
  QuestionPreviewImplProps,
} from "../Question";

import { Button, VStack } from "@chakra-ui/react";
import { Textarea, Radio, RadioGroup, Input } from "../../../wombat-ui";

import useTranslations from "../../../../hooks/useTranslations";
import QuestionTitle from "./utils/QuestionTitle";
import Answer from "./utils/Answer";
import useViewer from "../../../../hooks/useViewer";
import { AnswerFragment, QuestionFragment, SkillFragment } from '../../../../graphql/generated/definitions';
import QuestionDocuments from "./utils/QuestionDocuments";

type QuestionSkillOptions = {
  value: string;
  label: string;
  isGoal?: boolean;
};

type QuestionSkillValue = {
  selector: string;
  comment: string;
  timeSpent: number;
};

type QuestionSkill = {
  options: QuestionSkillOptions[];
  defaultValue: QuestionSkillValue;
  skill: SkillFragment;
} & QuestionFragment;

export function isQuestionSkill(q: QuestionFragment): q is QuestionSkill {
  return q.type === "QuestionSkill";
}

export function parseQuestion(question: QuestionFragment): QuestionSkill {
  return question as QuestionSkill;
}

type AnswerSkill = {
  value: QuestionSkillValue;
} & AnswerFragment;

export function parseAnswers(answers: AnswerFragment[]): AnswerSkill[] {
  return answers as AnswerSkill[];
}

type QuestionSkillProps = QuestionImplProps;

export const QuestionSkill: React.FC<QuestionSkillProps> = React.memo(
  ({
    question: rawQuestion,
    answers: rawAnswers,
    review,
    register,
    errors,
    toggleHistoryModalOpen,
    status,
    displayIndex,
    name,
    needsToBeFilled,
    onReview,
    autoFocus,
  }) => {
    const t = useTranslations();

    // FIXME: should be const question = rawQuestion as QuestionSkill ?
    const { id: viewerId } = useViewer();
    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);
    const answers = useMemo(
      () => getAnswersByRole(rawAnswers, parseAnswers, review, viewerId),
      [rawAnswers, viewerId]
    );

    const firstRadioRef = useRef(null);
    useEffect(() => {
      if (autoFocus) firstRadioRef.current?.focus();
    }, [firstRadioRef.current, autoFocus]);

    return (
      <div>
        <RadioGroup
          display="row"
          title={
            <QuestionTitle
              title={question.skill.name}
              required={question.required && status === "to_be_filled_collaborator"}
              description={question.description}
              displayIndex={displayIndex}
              referents={
                question.visibility === "collaborator" ? [] : review.referents
              }
            />
          }
          description={
            <VStack align="flex-start">
              <QuestionDocuments documents={question.documents} />
              <Button
                onClick={toggleHistoryModalOpen}
                variant="link"
                size="xs"
                id={`answerHistoryButton-${question.id}`}
              >
                {t("reports.answerHistory")}
              </Button>
            </VStack>
          }
        >
          {question.options.map((opt, index) => {
            return (
              <Radio
                defaultChecked={
                  answers?.mine?.[0]?.value?.selector === opt.value
                }
                className={
                  opt?.isGoal ? "text-warning-500 bg-warning-100" : undefined
                }
                key={`${opt.value}`}
                label={opt.label}
                help={
                  opt?.isGoal
                    ? (t("templates.targetLevel") as string)
                    : undefined
                }
                id={`${question.skill.name}_${opt.value}`}
                value={opt.value}
                disabled={!needsToBeFilled}
                variant="boxed"
                error={errors}
                name={`${name}.value.selector`}
                ref={(ref) => {
                  if (index === 0) firstRadioRef.current = ref;
                  register(ref, {
                    required: question.required && status === "to_be_filled_collaborator"
                      ? t("forms.errors.required") as string
                      : false,
                  });
                }}
              />
            );
          })}
        </RadioGroup>
        {question.timeTracked && (
          <Input
            label={
              !question.expectedTimeValue
                ? (t("reports.timeSpentInHour") as string)
                : `${t("reports.timeSpentInHour") as string} - ${t(
                    `reports.expectedTime.${question.expectedTimeUnit}`,
                    {
                      count: question.expectedTimeValue,
                    }
                  )}`
            }
            type="number"
            size="small"
            name={`${name}.timeSpent`}
            disabled={!needsToBeFilled}
            ref={register({ valueAsNumber: true })}
            id={`${question.id}-timeTracked`}
            defaultValue={answers?.collaborator?.[0]?.timeSpent || undefined}
          />
        )}
        {needsToBeFilled && (
          <div className="max-w-2xl pt-4">
            <Textarea
              defaultValue={answers?.mine?.[0]?.value?.comment}
              label={t("reports.comments") as string}
              id={question.id}
              rows={2}
              help={t("reports.commentsHelp") as string}
              ref={register()}
              name={`${name}.value.comment`}
            />
          </div>
        )}
        {(status === "to_be_filled_evaluator" || onReview) && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.collaboratorAnswer")}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.collaborator.length > 0 ? (
                answers.collaborator.map((a) => (
                  <Answer
                    answer={a}
                    withComment
                    comment={a.value.comment}
                    key={a.id}
                  />
                ))
              ) : (
                <p className="text-xs">{t("reports.noAnswer")}</p>
              )}
            </div>
          </div>
        )}
        {answers.referents.length > 0 && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.evaluatorsAnswers", {
                count: answers.referents.length,
              })}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.referents.length > 0 ? (
                answers.referents.map((a) => (
                  <Answer
                    answer={a}
                    withComment
                    comment={a.value.comment}
                    key={a.id}
                  />
                ))
              ) : (
                <p className="text-xs">{t("reports.noAnswer")}</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

type QuestionSkillPreviewProps = QuestionPreviewImplProps;

export const QuestionSkillPreview: React.FC<QuestionSkillPreviewProps> =
  React.memo(({ question: rawQuestion, displayIndex }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    return (
      <div>
        <RadioGroup
          display="row"
          title={
            <QuestionTitle
              title={question.skill.name}
              displayIndex={displayIndex}
            />
          }
          description={t("reports.skillQuestion")}
        >
          {question.options.map((opt) => (
            <Radio
              key={`${opt.value}`}
              name={question.id}
              label={opt.label}
              id={`${question.skill.name}_${opt.value}`}
              value={opt.value}
              disabled
              variant="boxed"
            />
          ))}
        </RadioGroup>
      </div>
    );
  });
